import React, { useEffect, useRef, useState, useMemo } from "react";
import Header from "../components/Header";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { addToCart } from "../../store/slice/cart";
import { toast } from "react-toastify";
import {
  handleGetAllChefs,
  handleGetSingleDish,
} from "../../services/get_without_auth";
import isValidURL from "../../ValidateUrl";
import { shefDetailPath } from "../../shefPath";
import { handleGetAvailabilityTimeSlot } from "../../services/shef";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import convertTo12Hour from "../../convertTo12Hours";
import Footer from "../components/Footer";
import { store } from "../../store/store";
import { handleGetAllReview, handleGetOrders } from "../../services/order";

export const DishDetailSingle = () => {
  // Description Tabs Start
  const [activeDescTab, setDescActiveTab] = useState(1);

  // Allowed Delivery date
  const [availableDates, setAvailableDates] = useState([]);

  // selected delivery date, and slot
  const [selectedDeliverDateAndSlot, setSelectedDeliveryDateAndSlot] = useState(
    {
      delivery_date: null, // Changed to null for better DatePicker handling
      delivery_slot: "",
    },
  );

  const descTabClick = (tabNumber) => {
    setDescActiveTab(tabNumber);
  };
  // Description Tabs End

  // Quantity management
  const [quantity, setQuantity] = useState(1);

  const handleIncrement = () => {
    setQuantity((prev) => prev + 1);
  };

  const handleDecrement = () => {
    setQuantity((prev) => (prev > 1 ? prev - 1 : 1));
  };

  const handleInputChange = (e) => {
    const value = parseInt(e.target.value, 10);
    if (!isNaN(value) && value >= 1) {
      setQuantity(value);
    }
  };

  // -- Get dishId from Param
  const { dishId } = useParams();
  // -- authtoken from redux store
  const { authToken } = useSelector((state) => state.user);
  // -- Single Dish
  const [dish, setDish] = useState({});
  // --- Api is fetching
  const [isFetching, setIsFetching] = useState(false);

  // ---- reference to delivery date & slot
  const deliveryDateRef = useRef(null);
  const deliverySlotRef = useRef(null);

  const { cartItem } = useSelector((state) => state.cart);

  const [reviews, setReviews] = useState([]);
  const [orders, setOrders] = useState([]);
  const [dishReviews, setDishReviews] = useState([]);

  // Memoized function to find existing cart quantity
  const existingCartQuantity = useMemo(() => {
    if (
      !dish?.chef?.id ||
      !selectedDeliverDateAndSlot.delivery_date ||
      !selectedDeliverDateAndSlot.delivery_slot
    ) {
      return 0;
    }

    const deliveryDateString =
      selectedDeliverDateAndSlot.delivery_date.toLocaleDateString();

    const chefInCart = cartItem?.find(
      (chef) =>
        chef.id === dish.chef.id &&
        chef.delivery_date === deliveryDateString &&
        chef.delivery_slot === selectedDeliverDateAndSlot.delivery_slot,
    );

    if (chefInCart) {
      const menuInCart = chefInCart.menu?.find((menu) => menu.id === dish.id);
      return menuInCart?.quantity || 0;
    }

    return 0;
  }, [
    cartItem,
    dish?.chef?.id,
    dish?.id,
    selectedDeliverDateAndSlot.delivery_date,
    selectedDeliverDateAndSlot.delivery_slot,
  ]);

  // Update quantity when cart changes or delivery details change
  useEffect(() => {
    if (existingCartQuantity > 0) {
      setQuantity(existingCartQuantity);
    } else {
      setQuantity(1);
    }
  }, [existingCartQuantity]);

  // Reset quantity when delivery date/slot changes to avoid confusion
  useEffect(() => {
    // When delivery details change, check if the new combination exists in the cart
    if (existingCartQuantity > 0) {
      setQuantity(existingCartQuantity);
    } else {
      // If not, reset quantity to 1
      setQuantity(1);
    }
  }, [
    selectedDeliverDateAndSlot.delivery_date,
    selectedDeliverDateAndSlot.delivery_slot,
    existingCartQuantity,
  ]);

  // Function to check available days including today in the next 7 to 14 days
  function getAvailableDates(
    availability,
    startDaysFromNow = 0,
    totalDays = 8,
  ) {
    const daysOfWeek = [
      "sunday",
      "monday",
      "tuesday",
      "wednesday",
      "thursday",
      "friday",
      "saturday",
    ];

    let currentDate = new Date();
    // Reset time to 00:00:00 to avoid timezone issues
    currentDate.setHours(0, 0, 0, 0);

    let availableDates = [];

    for (let i = startDaysFromNow; i < startDaysFromNow + totalDays; i++) {
      let futureDate = new Date();
      futureDate.setDate(currentDate.getDate() + i);
      futureDate.setHours(0, 0, 0, 0);

      let dayOfWeek = futureDate.getDay();
      const dayName = daysOfWeek[dayOfWeek];

      if (availability[`is_${dayName}`] === 1) {
        availableDates.push(new Date(futureDate));
      }
    }
    return availableDates;
  }

  const formatDateForCart = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");

    return `${year}-${month}-${day}`;
  };

  // Fetch Single Dish
  useEffect(() => {
    const fetchSingleDish = async () => {
      try {
        setIsFetching(true);
        const dishResponse = await handleGetSingleDish(dishId);
        console.log("Dish API Response:", dishResponse);
        const city = JSON.parse(localStorage.getItem("region"));
        const chefReponse = await handleGetAllChefs(city.id);

        const timeslotResponse = await handleGetAvailabilityTimeSlot();
        const formatedTimeSlotArray = timeslotResponse.map((time) => ({
          id: time.id,
          time_start: time.time_start.slice(0, 5),
          time_end: time.time_end.slice(0, 5),
        }));

        if (dishResponse.availability_time_slots?.length > 0) {
          dishResponse.availability_time_slots = formatedTimeSlotArray.filter(
            (time) =>
              dishResponse.availability_time_slots?.some(
                (av_slot) => av_slot.availability_time_slots_id === time.id,
              ),
          );
        } else {
          dishResponse.availability_time_slots = formatedTimeSlotArray;
        }

        chefReponse.forEach((chef) => {
          if (chef.id === dishResponse.user_id) {
            dishResponse.chef = chef;
          }
        });

        // Handle discounts
        if (dishResponse.auto_applied_active_discounts?.length > 0) {
          const discount = dishResponse.auto_applied_active_discounts[0];
          const discountAmount = parseFloat(discount.discount);
          let calculatedDiscount;

          if (discount.discount_type === "$") {
            calculatedDiscount = discountAmount;
          } else if (discount.discount_type === "%") {
            calculatedDiscount =
              dishResponse.chef_earning_fee * (discountAmount / 100);
          }

          dishResponse.chefFeeBeforeDiscount = dishResponse.chef_earning_fee;
          dishResponse.chef_earning_fee =
            dishResponse.chef_earning_fee - calculatedDiscount;
          dishResponse.discount = calculatedDiscount;
        }

        setDish(dishResponse);

        // Get available dates from the dish's availability
        const getNext14AvailableDays = getAvailableDates(dishResponse);
        setAvailableDates(getNext14AvailableDays);

        // Set initial delivery date to the first available date
        if (
          getNext14AvailableDays.length > 0 &&
          !selectedDeliverDateAndSlot.delivery_date
        ) {
          setSelectedDeliveryDateAndSlot((prev) => ({
            ...prev,
            delivery_date: getNext14AvailableDays[0],
          }));
        }
      } catch (error) {
        console.error("Error while fetching single dish \n", error);
      } finally {
        setIsFetching(false);
      }
    };
    fetchSingleDish();
  }, [authToken, dishId]);

  // add to cart
  const dispatch = useDispatch();
  const handleAddToCart = () => {
    // Validation
    if (!selectedDeliverDateAndSlot.delivery_date) {
      toast.error("Please pick the delivery date first.");
      deliveryDateRef.current?.setOpen(true);
      return;
    }

    if (!selectedDeliverDateAndSlot.delivery_slot) {
      deliverySlotRef.current?.focus();
      toast.error("Please pick the delivery slot first.");
      return;
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const selectedDate = new Date(selectedDeliverDateAndSlot.delivery_date);
    selectedDate.setHours(0, 0, 0, 0);

    // Compare only if delivery date is today
    if (selectedDate.getTime() === today.getTime()) {
      const [startTime, endTime] = selectedDeliverDateAndSlot.delivery_slot
        .split("-")
        .map((time) => time.trim());

      const [startHour, startMinute] = startTime.split(":").map(Number);
      const [endHour, endMinute] = endTime.split(":").map(Number);

      const currentHours = new Date().getHours();
      const currentMinutes = new Date().getMinutes();

      // Convert current time and slot times to minutes for easier comparison
      const currentTimeInMinutes = currentHours * 60 + currentMinutes;
      const startTimeInMinutes = startHour * 60 + startMinute;
      const endTimeInMinutes = endHour * 60 + endMinute;

      // Check if current time is within the slot range
      const isWithinSlot =
        currentTimeInMinutes >= startTimeInMinutes &&
        currentTimeInMinutes <= endTimeInMinutes;

      if (!isWithinSlot && currentTimeInMinutes > endTimeInMinutes) {
        toast.error(
          "This time slot has already passed. Please choose a future time.",
        );
        return;
      }
    }

    if (quantity <= 0) {
      toast.error("Quantity must be at least 1");
      return;
    }

    // Calculate unit price
    const unit_price = parseFloat(
      (
        dish.chef_earning_fee +
        dish.platform_price +
        dish.delivery_price
      ).toFixed(2),
    );

    const chef = {
      ...dish.chef,
      delivery_date:
         formatDateForCart(selectedDeliverDateAndSlot.delivery_date),
      delivery_slot: selectedDeliverDateAndSlot.delivery_slot,
    };

    const payload = {
      ...dish,
      quantity,
      unit_price,
      chef,
    };

    const state = store.getState().cart;
    if (!state.currentUserId || !state.cartInitialized) {
      console.error("Cannot add to cart");
      toast.error("Cart not initialized or user not logged in");
      return;
    }

    try {
      dispatch(addToCart(payload));
      toast.dismiss();
      toast.success(
        `${existingCartQuantity > 0 ? "Updated" : "Added to"} Cart`,
        {
          autoClose: 2000,
        },
      );
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: "add_to_cart",
      });
    } catch (error) {
      console.error("Error adding to cart:", error);
      toast.error("Failed to add item to cart");
    }
  };

  useEffect(() => {
    const fetchReviewsAndOrders = async () => {
      if (!authToken || !dish?.chef?.id) return;

      try {
        // 1. Get orders for this chef
        const ordersData = await handleGetOrders(authToken, {
          chef_id: dish.chef.id,
        });

        // 2. Get reviews for this chef
        const reviewsData = await handleGetAllReview(authToken, dish.chef.id);

        setOrders(ordersData);
        setReviews(reviewsData.data.original);

        // 3. Join reviews with orders
        const enrichedReviews = reviewsData.data.original.data.data.map(
          (review) => {
            const matchingOrder = ordersData.find(
              (o) => o.id === review.order_id,
            );

            return {
              reviewId: review.id,
              orderId: review.order_id,
              dishId: review.user_menu_id,
              dishName: review.user_menu?.name,
              rating: review.rating,
              review: review.review,
              createdAt: review.created_at,
              replies: review.replies,
              // from order → actual reviewer
              reviewerName: matchingOrder?.name || "Anonymous",
              reviewerEmail: matchingOrder?.email,
              reviewerPhone: matchingOrder?.phone,
            };
          },
        );

        // 4. Filter by dishId
        const filtered = enrichedReviews.filter(
          (r) => String(r.dishId) === String(dishId),
        );

        setDishReviews(filtered);
      } catch (error) {
        console.error("Error fetching reviews + orders", error);
      }
    };

    fetchReviewsAndOrders();
  }, [authToken, dish?.chef?.id, dishId]);

  const navigate = useNavigate();

  // Kitchen Offline — hide the dish while the chef's kitchen is offline
  if (!isFetching && dish?.user?.kitchen_offline) {
    return (
      <>
        <Header />
        <div className="container mx-auto my-6 lg:px-2 px-4">
          <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            This shef's kitchen is currently offline and not accepting orders.
            Please check back later.
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div>
        <Header />

        {isFetching && (
          <div className="fixed top-0 bottom-0 h-full w-[100vw] flex justify-center items-center bg-white bg-opacity-80 z-50">
            <h4 className="text-headGray">Loading...</h4>
          </div>
        )}

        <div className="container mx-auto my-6 lg:px-2 px-4">
          {/* Back Button */}
          <div className="cursor-pointer mb-5" onClick={() => navigate(-1)}>
            <div className="flex items-center gap-2">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="rgba(0,0,0,1)"
              >
                <path d="M7.82843 10.9999H20V12.9999H7.82843L13.1924 18.3638L11.7782 19.778L4 11.9999L11.7782 4.22168L13.1924 5.63589L7.82843 10.9999Z"></path>
              </svg>
              <div>
                <h1 className="text-lg font-semibold mb-0">Back</h1>
              </div>
            </div>
          </div>

          {/* ======== NEW LAYOUT START ======== */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 xl:gap-12">
            {/* --- LEFT COLUMN: IMAGE --- */}
            <div className="lg:col-span-5">
              <div className="relative h-full min-h-[300px] lg:min-h-[400px]">
                <img
                  src={
                    dish.logo && isValidURL(dish.logo)
                      ? dish.logo
                      : "https://t4.ftcdn.net/jpg/04/70/29/97/360_F_470299797_UD0eoVMMSUbHCcNJCdv2t8B2g1GVqYgs.jpg"
                  }
                  className="img-fluid object-cover h-full w-full rounded-xl shadow-lg"
                  alt="product-img"
                />
              </div>
            </div>

            {/* --- RIGHT COLUMN: DETAILS & ACTIONS --- */}
            <div className="lg:col-span-7">
              {/* Block 1: Main Info (Name, Chef, Price) */}
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div className="flex-1">
                  <h2 className="text-3xl md:text-4xl text-secondary font-bold leading-tight">
                    {dish.name}
                  </h2>
                  <div className="text-lg text-secondary flex items-center flex-wrap gap-x-3 gap-y-1 mt-2">
                    <span className="!text-headGray">by</span>
                    <Link
                      to={shefDetailPath(dish?.user_id, dish?.chef?.first_name, dish?.chef?.last_name)}
                      className="!underline !text-headGray font-semibold hover:text-primary"
                    >
                      {`${dish?.chef?.first_name || ""} ${dish?.chef?.last_name || ""}`}
                    </Link>
                    <span className="!text-headGray">| rating</span>
                    <div className="inline-flex gap-x-2 items-center bg-[#ffc00047] px-2 py-1 rounded-[4px]">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="12"
                        height="12"
                        fill="#323232"
                      >
                        <path d="M12.0006 18.26L4.94715 22.2082L6.52248 14.2799L0.587891 8.7918L8.61493 7.84006L12.0006 0.5L15.3862 7.84006L23.4132 8.7918L17.4787 14.2799L19.054 22.2082L12.0006 18.26Z"></path>
                      </svg>
                      <h4 className="text-base leading-tight mb-0 font-semibold">
                        {dish?.average_rating
                          ? parseFloat(dish.average_rating).toFixed(1)
                          : 0}
                        <span className="text-[12px] font-normal">
                          {" "}
                          ({dish?.total_reviews || 0}){" "}
                        </span>
                      </h4>
                    </div>
                  </div>
                </div>
                <div className="text-left sm:text-right">
                  <h2 className="text-3xl md:text-4xl text-secondary font-bold">
                    {(
                      dish.chef_earning_fee +
                      dish.platform_price +
                      dish.delivery_price
                    ).toLocaleString("en-PK", {
                      style: "currency",
                      currency: "PKR",
                    })}
                  </h2>
                  {dish?.auto_applied_active_discounts?.length > 0 && (
                    <>
                      <del className="text-base md:text-lg text-red-400">
                        {(
                          dish.chefFeeBeforeDiscount +
                          dish.platform_price +
                          dish.delivery_price
                        ).toLocaleString("en-PK", {
                          style: "currency",
                          currency: "PKR",
                        })}
                      </del>
                      <span className="text-base md:text-lg block -mt-1 text-green-700 font-semibold">
                        [{" "}
                        {dish?.discount?.toLocaleString("en-PK", {
                          style: "currency",
                          currency: "PKR",
                        })}{" "}
                        <span className="text-sm">Off</span> ]
                      </span>
                    </>
                  )}
                </div>
              </div>
              <hr className="my-6" />

              {/* Block 2: Configuration & Actions Card */}
              <div className="border rounded-xl p-4 lg:p-6 space-y-6 bg-gray-50/50">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Portion Size */}
                  <div>
                    <h4 className="text-base font-bold uppercase mb-2">
                      Portion size
                    </h4>
                    <div className="border border-borderClr rounded-lg p-3 h-full flex items-center">
                      <div className="flex items-center gap-3 w-full">
                        <div className="text-center border-r pr-3">
                          <div className="mb-1">
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              viewBox="0 0 24 24"
                              width="24"
                              height="24"
                              fill="currentColor"
                              className="text-gray-500"
                            >
                              <path d="M17.618 5.966L19.032 4.552L20.448 5.966L19.032 7.38L17.618 5.966ZM12 2C11.448 2 11 2.448 11 3V4.052C6.834 4.557 3.5 7.874 3 12H2C2 11.448 1.552 11 1 11C0.448 11 0 11.448 0 12C0 12.552 0.448 13 1 13C1.552 13 2 12.552 2 12H3C3.5 17.514 8.486 22 14 22C19.514 22 24 17.514 24 12C24 6.833 20.443 2.504 15.948 2.052V3C15.948 3.552 15.499 4 14.948 4C14.398 4 13.948 3.552 13.948 3V2.052C13.141 2.018 12.553 2 12 2ZM14 6.052C18.393 6.556 22 10.082 22 14C22 15.343 21.61 16.59 20.949 17.674L9.308 6.033C10.409 5.381 11.657 5 13 5V6.052H14Z"></path>
                            </svg>
                          </div>
                          <h4 className="text-[10px] font-semibold mb-0 leading-tight">
                            {dish?.portion_type?.name}
                          </h4>
                        </div>
                        <div className="flex-1">
                          <h5 className="text-base font-semibold mb-0 leading-snug">
                            {dish.portion_size}{" "}
                            {dish.base_type_id === 1
                              ? "grams Container"
                              : dish.base_type_id === 2
                                ? "pieces"
                                : "Other"}
                          </h5>
                          <h6 className="text-[12px] text-primaryGreen mb-0">
                            Shef recommend
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Spice Level */}
                  <div>
                    <h4 className="text-base font-bold uppercase mb-2">
                      Spice level
                    </h4>
                    <div className="border border-borderClr rounded-lg p-3 h-full flex items-center">
                      <div className="flex items-center gap-3 w-full">
                        <div className="border-r pr-3">
                          <svg
                            className="sc-jSgupP ckDfJz"
                            height="24"
                            width="24"
                            viewBox="0 0 16 16"
                            version="1.1"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="currentColor"
                          >
                            <path
                              d="M13.9834 5.93983L13.879 5.37787C13.6812 4.30918 13.0436 3.38775 12.1306 2.8507C12.0895 2.82688 11.9106 2.73918 11.7018 2.64822C11.8484 2.32664 11.9128 1.97258 11.8862 1.61202C11.844 1.03058 11.5718 0.500021 11.1197 0.118889C10.9109 -0.0576019 10.5954 -0.0359466 10.4144 0.167613C10.2333 0.371172 10.2566 0.678677 10.4644 0.855168C10.7143 1.06631 10.8654 1.35974 10.8887 1.68132C10.9065 1.91736 10.8531 2.1469 10.7387 2.3483C9.27136 2.10576 7.76733 2.67421 6.82315 3.85767L6.57544 4.16843C6.30551 4.50733 6.28996 4.96859 6.53101 5.30641C6.29774 5.96256 6.22332 6.71725 6.14778 7.51092C6.04003 8.62725 5.92895 9.78255 5.38688 10.887C5.02032 11.633 4.58821 12.1852 4.10279 12.5263C3.90507 12.666 3.66514 12.7721 3.37189 12.8522C2.6021 13.0622 2.11001 13.5441 2.02115 14.1721C1.94784 14.6875 2.07002 15.1596 2.36328 15.5017C2.61987 15.7995 2.98755 15.9792 3.373 15.9933C3.47852 15.9976 3.58405 15.9987 3.69069 15.9987C5.81787 15.9987 8.00838 15.218 9.749 13.8332C11.2097 12.6692 13.0125 10.5459 13.4458 6.98144C13.4757 6.96737 13.5057 6.95329 13.5346 6.93597C13.8756 6.73566 14.0556 6.33503 13.9823 5.93874L13.9834 5.93983ZM7.61182 4.45644C8.37716 3.49711 9.67902 3.05967 10.852 3.36718C11.0275 3.41374 11.5141 3.63462 11.6218 3.68984C12.285 4.08397 12.7493 4.76178 12.8959 5.5522L12.9925 6.08383L12.3561 5.79798C11.7218 5.51322 10.9787 5.62258 10.4588 6.07625L9.92562 6.49637C9.90229 6.51477 9.87563 6.5191 9.84675 6.51044C9.81787 6.50178 9.79899 6.48337 9.79121 6.45522L9.56794 5.69837L9.55572 5.66156C9.36244 5.16024 8.88369 4.81917 8.33495 4.7921L7.38188 4.74554L7.61182 4.45752V4.45644ZM9.11585 13.0796C7.47519 14.3865 5.39688 15.0946 3.41076 15.0199C3.30857 15.0166 3.20304 14.9625 3.12862 14.8759C3.01643 14.7459 2.97533 14.5489 3.01087 14.3053C3.0542 13.9956 3.4252 13.8494 3.64181 13.7909C4.04503 13.6805 4.39716 13.5213 4.68819 13.3167C5.31024 12.8793 5.84898 12.2025 6.28885 11.3082C6.90757 10.0478 7.03309 8.74852 7.14306 7.60295C7.21193 6.884 7.27525 6.24733 7.44409 5.72327L8.28608 5.76442C8.43159 5.772 8.55822 5.85862 8.61487 5.98855L8.83148 6.72266C8.93256 7.06374 9.20027 7.33118 9.54684 7.43837C9.65458 7.47194 9.76455 7.48818 9.87341 7.4871C10.1178 7.4871 10.3577 7.40589 10.5532 7.25214L11.0975 6.82336L11.1208 6.80387C11.343 6.60573 11.664 6.55808 11.9373 6.68044L12.4483 6.90998C12.0439 10.1258 10.4288 12.0336 9.11696 13.0785L9.11585 13.0796Z"
                              fill="currentColor"
                              stroke="none"
                              className="sc-iBPRYJ brBpOV"
                            ></path>
                          </svg>
                        </div>
                        <div className="flex-1">
                          <h5 className="text-base font-semibold mb-0 leading-snug">
                            {dish?.spice_level?.name}
                          </h5>
                          <h6 className="text-[12px] text-primaryGreen mb-0">
                            Shef recommend
                          </h6>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                {/* Delivery Date & Slot */}
                <div>
                  <h4 className="text-base font-bold uppercase mb-3">
                    Schedule Your Delivery
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="relative">
                      <label className="text-sm font-semibold text-headGray mb-1 block">
                        Delivery Date <span className="text-primary">*</span>
                      </label>
                      <DatePicker
                        ref={deliveryDateRef}
                        required
                        wrapperClassName="w-full"
                        popperClassName="!z-50"
                        popperPlacement="top-start"
                        selected={selectedDeliverDateAndSlot.delivery_date}
                        onChange={(date) => {
                          setSelectedDeliveryDateAndSlot((prev) => ({
                            ...prev,
                            delivery_date: date,
                          }));
                        }}
                        includeDates={availableDates}
                        minDate={new Date()} // Can't select past dates
                        placeholderText="Select a delivery date"
                        dateFormat="MMMM d, yyyy"
                        className="w-full p-3 border border-borderClr rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                        filterDate={(date) => {
                          // Check if the date is in availableDates
                          const dateString = date.toDateString();
                          return availableDates.some(
                            (availableDate) =>
                              availableDate.toDateString() === dateString,
                          );
                        }}
                      />
                    </div>
                    <div className="relative">
                      <label className="text-sm font-semibold text-headGray mb-1 block">
                        Delivery Time <span className="text-primary">*</span>
                      </label>
                      <select
                        className="w-full p-3 border border-borderClr rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                        ref={deliverySlotRef}
                        required
                        onChange={(e) =>
                          setSelectedDeliveryDateAndSlot((prev) => ({
                            ...prev,
                            delivery_slot: e.target.value,
                          }))
                        }
                        value={selectedDeliverDateAndSlot.delivery_slot}
                      >
                        <option value="">Select delivery slot</option>
                        {dish?.availability_time_slots?.map((slot, index) => (
                          <option
                            key={index}
                            value={`${slot.time_start}-${slot.time_end}`}
                          >
                            {`${convertTo12Hour(slot.time_start)} - ${convertTo12Hour(slot.time_end)}`}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
                {/* Quantity & Add to Cart */}
                <div className="flex flex-col sm:flex-row items-center gap-4 pt-4 border-t">
                  <div className="w-full sm:w-auto">
                    <div className="flex items-center justify-between bg-grayBg rounded-lg max-w-[150px] w-full mx-auto">
                      <button
                        onClick={handleDecrement}
                        disabled={quantity <= 1}
                        className="w-1/3 p-2 disabled:opacity-60 hover:bg-gray-200 rounded-l-lg transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mx-auto"
                          viewBox="0 0 24 24"
                          width="18"
                          height="18"
                          fill="rgba(0,0,0,1)"
                        >
                          <path d="M5 11V13H19V11H5Z"></path>
                        </svg>
                      </button>
                      <div className="w-1/3">
                        <input
                          className="text-center w-full border-0 bg-transparent text-lg font-semibold px-1 focus:ring-0"
                          value={quantity}
                          onChange={handleInputChange}
                          type="number"
                          min="1"
                        />
                      </div>
                      <button
                        onClick={handleIncrement}
                        className="w-1/3 p-2 hover:bg-gray-200 rounded-r-lg transition-colors"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mx-auto"
                          viewBox="0 0 24 24"
                          width="18"
                          height="18"
                          fill="rgba(0,0,0,1)"
                        >
                          <path d="M11 11V5H13V11H19V13H13V19H11V13H5V11H11Z"></path>
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="flex-grow w-full sm:w-auto">
                    <button
                      onClick={handleAddToCart}
                      disabled={
                        isFetching ||
                        !selectedDeliverDateAndSlot.delivery_date ||
                        !selectedDeliverDateAndSlot.delivery_slot
                      }
                      className="text-lg font-bold bg-primary w-full h-full uppercase text-white rounded-lg py-3 px-6 disabled:opacity-60 disabled:cursor-not-allowed hover:bg-opacity-90 transition-opacity"
                    >
                      {existingCartQuantity > 0 ? "Update Cart" : "Add to Cart"}
                    </button>
                  </div>
                </div>
              </div>

              {/* Block 3: Descriptive Info (Availability, Tabs) */}
              <div className="mt-8">
                {/* Availibility */}
                <div className="mb-6">
                  <h4 className="text-base font-bold uppercase mb-2 text-headGray">
                    Weekly Availability
                  </h4>
                  <ul className="flex gap-2 flex-wrap">
                    {[
                      "Sunday",
                      "Monday",
                      "Tuesday",
                      "Wednesday",
                      "Thursday",
                      "Friday",
                      "Saturday",
                    ].map((day, index) => (
                      <div className="relative group" key={day}>
                        <li
                          className={`text w-8 h-8 text-center ${dish?.[`is_${day.toLowerCase()}`] === 1 ? "bg-primary text-white" : "bg-gray-100 text-gray-400"} rounded-full mb-0 flex items-center justify-center font-medium`}
                        >
                          {day.charAt(0)}
                        </li>
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-gray-700 text-white text-sm rounded-md opacity-0 group-hover:opacity-100 transition-opacity duration-300 whitespace-nowrap z-10 pointer-events-none">
                          {day}
                        </div>
                      </div>
                    ))}
                  </ul>
                </div>
                {/* Tabs */}
                <div>
                  <div className="flex gap-4 mb-4 border-b">
                    <div
                      onClick={() => descTabClick(1)}
                      className={`pb-2 ${activeDescTab === 1 ? "border-b-2 border-primary text-primary" : ""} cursor-pointer font-semibold text-lg transition-colors`}
                    >
                      Description
                    </div>

                    <div
                      onClick={() => descTabClick(3)}
                      className={`pb-2 ${activeDescTab === 3 ? "border-b-2 border-primary text-primary" : ""} cursor-pointer font-semibold text-lg transition-colors`}
                    >
                      Reviews
                    </div>
                  </div>
                  <div>
                    {activeDescTab === 1 && (
                      <p className="text-base break-words whitespace-pre-line">
                        {dish.description || "No description available."}
                      </p>
                    )}
                    {activeDescTab === 2 && (
                      <div className="text-base whitespace-pre-line">
                        {dish?.reheating_instruction ? (
                          dish.reheating_instruction
                        ) : (
                          <p>No preparation instructions available.</p>
                        )}
                      </div>
                    )}
                    {activeDescTab === 3 && (
                      <div className="mt-2">
                        {dishReviews.length > 0 ? (
                          <>
                            <div className="space-y-4 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                              {dishReviews.map((r, index) => (
                                <div
                                  key={r.reviewId || index}
                                  className="group relative bg-white border border-gray-100 rounded-lg p-4 shadow-sm hover:shadow-md transition-all duration-200 hover:border-gray-200"
                                >
                                  <div className="flex items-center justify-between mb-3">
                                    <div className="flex items-center gap-3">
                                      <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/80 rounded-full flex items-center justify-center text-white font-semibold text-sm">
                                        {(r.reviewerName || "A")
                                          .charAt(0)
                                          .toUpperCase()}
                                      </div>
                                      <div>
                                        <p className="font-semibold text-gray-800 text-sm mb-0">
                                          {r.reviewerName || "Anonymous User"}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                          Verified Customer
                                        </p>
                                      </div>
                                    </div>

                                    {r.rating && (
                                      <div className="flex items-center gap-1">
                                        {[1, 2, 3, 4, 5].map((star) => (
                                          <svg
                                            key={star}
                                            className={`w-3 h-3 ${
                                              star <= r.rating
                                                ? "text-yellow-400 fill-current"
                                                : "text-gray-300"
                                            }`}
                                            xmlns="http://www.w3.org/2000/svg"
                                            viewBox="0 0 24 24"
                                          >
                                            <path d="M12.0006 18.26L4.94715 22.2082L6.52248 14.2799L0.587891 8.7918L8.61493 7.84006L12.0006 0.5L15.3862 7.84006L23.4132 8.7918L17.4787 14.2799L19.054 22.2082L12.0006 18.26Z"></path>
                                          </svg>
                                        ))}
                                      </div>
                                    )}
                                  </div>

                                  <div className="relative">
                                    <p className="text-gray-700 text-sm leading-relaxed line-clamp-3 group-hover:line-clamp-none transition-all duration-200">
                                      "{r.review}"
                                    </p>

                                    <div className="absolute -top-2 -left-1 text-gray-200 text-2xl font-serif leading-none pointer-events-none">
                                      "
                                    </div>
                                  </div>

                                  {r.createdAt && (
                                    <div className="mt-3 pt-2 border-t border-gray-100">
                                      <p className="text-xs text-gray-400">
                                        {new Date(
                                          r.createdAt,
                                        ).toLocaleDateString("en-US", {
                                          year: "numeric",
                                          month: "long",
                                          day: "numeric",
                                        })}
                                      </p>
                                    </div>
                                  )}

                                  {r.helpful && (
                                    <div className="absolute top-3 right-3">
                                      <span className="inline-flex items-center gap-1 bg-green-50 text-green-700 text-xs px-2 py-1 rounded-full">
                                        <svg
                                          className="w-3 h-3"
                                          fill="currentColor"
                                          viewBox="0 0 20 20"
                                        >
                                          <path
                                            fillRule="evenodd"
                                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                            clipRule="evenodd"
                                          />
                                        </svg>
                                        Helpful
                                      </span>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>

                            {dishReviews.length > 5 && (
                              <div className="mt-4 text-center">
                                <button className="text-primary hover:text-primary/80 text-sm font-medium underline transition-colors duration-200">
                                  View all reviews
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="text-center py-12">
                            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                              <svg
                                className="w-8 h-8 text-gray-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={1.5}
                                  d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                                />
                              </svg>
                            </div>
                            <h6 className="text-gray-500 font-medium mb-2">
                              No reviews yet
                            </h6>
                            <p className="text-gray-400 text-sm max-w-sm mx-auto leading-relaxed">
                              Be the first to share your experience with this
                              delicious dish. Your feedback helps other food
                              lovers make great choices!
                            </p>
                            <button className="mt-4 text-primary hover:text-primary/80 text-sm font-medium underline transition-colors duration-200">
                              Write the first review
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                    <style>{`
                        .custom-scrollbar::-webkit-scrollbar { width: 6px; }
                        .custom-scrollbar::-webkit-scrollbar-track { background: #f1f1f1; border-radius: 3px; }
                        .custom-scrollbar::-webkit-scrollbar-thumb { background: #c1c1c1; border-radius: 3px; }
                        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: #a8a8a8; }
                        .line-clamp-3 { display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
                        .line-clamp-none { display: block; -webkit-line-clamp: none; }
                      `}</style>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* ======== NEW LAYOUT END ======== */}
        </div>
      </div>
      <Footer />
    </>
  );
};

export default DishDetailSingle;
